import React, { useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import PicStudioLogo from '@components/PicStudioLogo/PicStudioLogo';
import { Button } from '@components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@components/ui/card';
import { CircleAlert } from 'lucide-react';
import * as Sentry from '@sentry/nextjs';

interface ErrorBoundaryFullPageProps {
  error: Error & { digest?: string };
  reset: () => void;
  title?: string;
  description?: string;
}

export default function ErrorBoundaryFullPage({
  error,
  reset,
  title = 'Something went wrong!',
  description = "We encountered an unexpected error. Don't worry, our team has been notified and is working on it."
}: ErrorBoundaryFullPageProps) {
  useEffect(() => {
    // Log the error to Sentry
    Sentry.captureException(error);
  }, [error]);

  // List of images for visual interest
  const images = ['/images/nerdy.webp', '/images/nerdy1.webp'];
  const randomImage = images[Math.floor(Math.random() * images.length)] as string;

  return (
    <div className="grid min-h-screen grid-cols-1 grid-rows-[1fr,auto,1fr] bg-muted lg:grid-cols-[max(50%,36rem),1fr]">
      <header className="mx-auto w-full max-w-7xl px-6 pt-6 sm:pt-10 lg:col-span-2 lg:col-start-1 lg:row-start-1 lg:px-8">
        <Link href="/">
          <PicStudioLogo />
        </Link>
      </header>
      <main className="mx-auto w-full max-w-7xl px-6 py-24 sm:py-32 lg:col-span-2 lg:col-start-1 lg:row-start-2 lg:px-8">
        <div className="max-w-lg">
          <div className="block lg:hidden p-3 pb-8 bg-studio-200 dark:bg-white w-fit mb-6 rounded-sm -rotate-2">
            <div className="aspect-square overflow-hidden w-32 h-32 rounded-sm">
              <Image src={randomImage} alt="" width={3000} height={3000} className="" />
            </div>
          </div>

          <div className="flex items-center gap-2 mb-4">
            <CircleAlert className="h-6 w-6 text-destructive" />
            <p className="text-base font-semibold leading-8 text-destructive">Error</p>
          </div>

          <h1 className="mt-4 text-3xl font-bold tracking-tight text-primary sm:text-5xl">{title}</h1>

          <p className="mt-6 text-base leading-7 text-muted-foreground">{description}</p>

          {process.env.NODE_ENV === 'development' && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-sm">Error Details (Development)</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-xs font-mono">{error.message}</CardDescription>
                {error.digest && (
                  <CardDescription className="text-xs text-muted-foreground mt-2">
                    Error ID: {error.digest}
                  </CardDescription>
                )}
              </CardContent>
            </Card>
          )}

          <div className="mt-10 flex gap-4">
            <Button onClick={reset} variant="default">
              Try again
            </Button>
            <Button asChild variant="outline">
              <Link href="/">
                <span aria-hidden="true">&larr;</span> Back to home
              </Link>
            </Button>
          </div>
        </div>
      </main>

      <footer className="self-end lg:col-span-2 lg:col-start-1 lg:row-start-3">
        <div className="bg-muted border-t border-muted py-10">
          <nav className="mx-auto flex w-full max-w-7xl items-center gap-x-4 px-6 text-sm leading-7 text-muted-foreground lg:px-8">
            <Link href="https://twitter.com/picstudioai" target="_blank">
              Twitter
            </Link>
          </nav>
        </div>
      </footer>

      <div className="hidden lg:relative lg:col-start-2 lg:row-start-1 lg:row-end-4 lg:block bg-black">
        <Image
          src={randomImage}
          alt=""
          width={3000}
          height={3000}
          className="absolute inset-0 h-full w-full object-cover opacity-50"
        />
      </div>
    </div>
  );
}
